import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import Skeleton from 'app/shared/components/common/Skeleton';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  AiProcessExecutionStatus,
  LoadStatus,
  RUNS_DEFAULT_PAGE_SIZE,
} from 'constants/aiProcessRuns';
import { SKELETON_TYPES } from 'constants/skeleton';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  loadRunsForOwnerAction,
  selectExecutionIdsNeedingReview,
  selectFiltersForOwner,
  selectNeedsReviewCount,
  selectProcessesUsedByOwner,
  selectRunsByOwner,
  selectRunsLoadStatusByOwner,
  selectRunsTotalCountByOwner,
  setRunsPageAction,
} from 'store/aiProcessRuns';
import { getUuid } from 'store/auth/selectors/master.selectors';

import {
  ALL_STATUSES_VALUE,
  DateGroupLabel,
  GroupByMode,
  RUN_HISTORY_TITLE,
  SAVED_FILTER_CHIPS,
  SavedFilterChipId,
  STATUS_FILTER_OPTIONS,
} from '../AiActivity.constants';
import { AiActivityTestIds } from '../AiActivity.testIds';
import {
  computeProcessGroupStats,
  formatDurationSeconds,
  groupRunsByDate,
  groupRunsByProcess,
} from '../AiActivity.utils';
import AiActivityEmptyState from '../AiActivityEmptyState';
import RunRow from '../RunRow';
import StatusFilterChip from '../StatusFilterChip';

import FilterField from './components/FilterField';
import { FILTER_FIELDS, FilterFieldKey } from './RunHistoryCard.config';
import {
  CLEAR_FILTERS_LABEL,
  EMPTY_STATE_SUBTITLE,
  EMPTY_STATE_TITLE,
  ERROR_STATE_SUBTITLE,
  ERROR_STATE_TITLE,
  FIRST_RUN_SUBTITLE,
  FIRST_RUN_TITLE,
  formatNeedsReviewChipLabel,
  PAGER_SHOWING_TEMPLATE,
  RETRY_LABEL,
  RUN_SKELETON_ROW_COUNT,
  RUN_SKELETON_ROW_HEIGHT,
  SAVED_CHIP_LABEL_PREFIX,
  SEARCH_PLACEHOLDER,
  START_PROCESS_LABEL,
} from './RunHistoryCard.constants';
import {
  chipRowStyles,
  filterBarStyles,
  filterTopRowStyles,
  groupByToggleRowStyles,
  groupHeaderStyles,
  groupMetaStyles,
  groupSectionStyles,
  groupTitleStyles,
  pagerButtonsStyles,
  paginationRowStyles,
  rowsListStyles,
  savedChipRowStyles,
  statusGroupStyles,
} from './RunHistoryCard.styles';
import { RunHistoryCardTestIds } from './RunHistoryCard.testIds';
import { applyClientSideFilters, dateValueToIso, formatRunCount } from './RunHistoryCard.utils';

const RunHistoryCard = ({
  filters,
  ownerKind,
  ownerId,
  onFiltersChange,
  onSelectExecution,
  isStartAllowed = false,
  onStartAiProcessClick = null,
}) => {
  const dispatch = useDispatch();

  const runs = useSelector(selectRunsByOwner(ownerId));
  const totalCount = useSelector(selectRunsTotalCountByOwner(ownerId));
  const loadStatus = useSelector(selectRunsLoadStatusByOwner(ownerId));
  const processes = useSelector(selectProcessesUsedByOwner(ownerId));
  const slicedFilters = useSelector(selectFiltersForOwner(ownerId));
  const currentUserId = useSelector(getUuid);
  const needsReviewExecutionIds = useSelector(selectExecutionIdsNeedingReview);
  const needsReviewCount = useSelector(selectNeedsReviewCount);

  const [expandedRowId, setExpandedRowId] = useState(null);
  const [savedChips, setSavedChips] = useState([]);
  const [searchText, setSearchText] = useState(filters.search ?? '');

  const processesById = useMemo(
    () => Object.fromEntries((processes ?? []).map(item => [item.id, item])),
    [processes],
  );

  const groupBy = filters.groupBy ?? GroupByMode.DATE;
  const currentStatus = filters.status ?? ALL_STATUSES_VALUE;
  const currentTake = filters.take ?? RUNS_DEFAULT_PAGE_SIZE;
  const currentSkip = filters.skip ?? 0;
  const currentPage = currentTake > 0 ? Math.floor(currentSkip / currentTake) : 0;

  const visibleRuns = useMemo(
    () =>
      applyClientSideFilters({
        runs,
        savedChips,
        searchText,
        processesById,
        currentUserId,
        needsReviewExecutionIds,
      }),
    [runs, savedChips, searchText, processesById, currentUserId, needsReviewExecutionIds],
  );

  const dateGroups = useMemo(
    () => (groupBy === GroupByMode.DATE ? groupRunsByDate(visibleRuns) : []),
    [groupBy, visibleRuns],
  );

  const processGroups = useMemo(
    () => (groupBy === GroupByMode.PROCESS ? groupRunsByProcess(visibleRuns, processesById) : []),
    [groupBy, visibleRuns, processesById],
  );

  const processOptions = useMemo(
    () => (processes ?? []).map(process => ({ value: process.id, label: process.name })),
    [processes],
  );

  const currentProcessOption = processOptions.find(opt => opt.value === filters.processId) ?? null;

  const onSearchChange = useCallback(event => {
    const next = event?.target?.value ?? '';

    setSearchText(next);
  }, []);

  const onProcessChange = useCallback(
    (_event, option) => {
      onFiltersChange({ processId: option?.value ?? null, skip: 0 });
    },
    [onFiltersChange],
  );

  const onStartedAfterChange = useCallback(
    value => {
      onFiltersChange({ startedAfter: dateValueToIso(value), skip: 0 });
    },
    [onFiltersChange],
  );

  const onStartedBeforeChange = useCallback(
    value => {
      onFiltersChange({ startedBefore: dateValueToIso(value), skip: 0 });
    },
    [onFiltersChange],
  );

  const onStatusChipClick = useCallback(
    value => {
      const nextStatus = value === ALL_STATUSES_VALUE ? null : value;

      onFiltersChange({ status: nextStatus, skip: 0 });
    },
    [onFiltersChange],
  );

  const onPrevPage = useCallback(() => {
    if (currentPage === 0) {
      return;
    }

    const nextSkip = (currentPage - 1) * currentTake;

    dispatch(setRunsPageAction({ ownerId, skip: nextSkip }));
    onFiltersChange({ skip: nextSkip });
  }, [currentPage, currentTake, dispatch, ownerId, onFiltersChange]);

  const onNextPage = useCallback(() => {
    const nextSkip = (currentPage + 1) * currentTake;

    if (!_.isNil(totalCount) && nextSkip >= totalCount) {
      return;
    }

    dispatch(setRunsPageAction({ ownerId, skip: nextSkip }));
    onFiltersChange({ skip: nextSkip });
  }, [currentPage, currentTake, dispatch, ownerId, onFiltersChange, totalCount]);

  const onSavedChipToggle = useCallback(
    chipId => () => {
      setSavedChips(current =>
        current.includes(chipId) ? current.filter(id => id !== chipId) : [...current, chipId],
      );
    },
    [],
  );

  const onGroupByDate = useCallback(() => {
    onFiltersChange({ groupBy: GroupByMode.DATE });
  }, [onFiltersChange]);

  const onGroupByProcess = useCallback(() => {
    onFiltersChange({ groupBy: GroupByMode.PROCESS });
  }, [onFiltersChange]);

  const onRunRowExpandToggle = useCallback(
    runId => setExpandedRowId(prev => (prev === runId ? null : runId)),
    [],
  );

  const onRunRowOpenDrawer = useCallback(runId => onSelectExecution(runId), [onSelectExecution]);

  const onRetryLoad = useCallback(() => {
    dispatch(loadRunsForOwnerAction({ ownerKind, ownerId }));
  }, [dispatch, ownerKind, ownerId]);

  const onClearFilters = useCallback(() => {
    setSearchText('');
    setSavedChips([]);
    onFiltersChange({
      status: null,
      processId: null,
      startedAfter: null,
      startedBefore: null,
      skip: 0,
    });
  }, [onFiltersChange]);

  const filterValuesByKey = useMemo(
    () => ({
      [FilterFieldKey.SEARCH]: searchText,
      [FilterFieldKey.PROCESS]: currentProcessOption,
      [FilterFieldKey.STARTED_AFTER]: filters.startedAfter ?? null,
      [FilterFieldKey.STARTED_BEFORE]: filters.startedBefore ?? null,
    }),
    [searchText, currentProcessOption, filters.startedAfter, filters.startedBefore],
  );

  const filterChangeHandlersByKey = useMemo(
    () => ({
      [FilterFieldKey.SEARCH]: onSearchChange,
      [FilterFieldKey.PROCESS]: onProcessChange,
      [FilterFieldKey.STARTED_AFTER]: onStartedAfterChange,
      [FilterFieldKey.STARTED_BEFORE]: onStartedBeforeChange,
    }),
    [onSearchChange, onProcessChange, onStartedAfterChange, onStartedBeforeChange],
  );

  const filterFieldDataTestIdsByKey = useMemo(
    () => ({
      [FilterFieldKey.SEARCH]: AiActivityTestIds.SEARCH_INPUT,
    }),
    [],
  );

  const filterFieldPlaceholdersByKey = useMemo(
    () => ({
      [FilterFieldKey.SEARCH]: SEARCH_PLACEHOLDER,
    }),
    [],
  );

  const filterFieldOptionsByKey = useMemo(
    () => ({
      [FilterFieldKey.PROCESS]: processOptions,
    }),
    [processOptions],
  );

  const renderRunRow = useCallback(
    run => (
      <RunRow
        isExpanded={expandedRowId === run.id}
        key={run.id}
        processesById={processesById}
        run={run}
        onExpandToggle={onRunRowExpandToggle}
        onOpenDrawer={onRunRowOpenDrawer}
      />
    ),
    [expandedRowId, processesById, onRunRowExpandToggle, onRunRowOpenDrawer],
  );

  const pagerLabel = useMemo(
    () =>
      PAGER_SHOWING_TEMPLATE({
        from: visibleRuns.length === 0 ? 0 : currentSkip + 1,
        to: currentSkip + visibleRuns.length,
        total: totalCount ?? 0,
      }),
    [currentSkip, totalCount, visibleRuns.length],
  );

  const hasActiveFilters =
    currentStatus !== ALL_STATUSES_VALUE ||
    !_.isNil(filters.processId) ||
    !_.isNil(filters.startedAfter) ||
    !_.isNil(filters.startedBefore) ||
    savedChips.length > 0 ||
    searchText.trim() !== '';

  const isInitialLoading = loadStatus === LoadStatus.LOADING && runs.length === 0;
  const isLoadError = loadStatus === LoadStatus.ERROR && runs.length === 0;
  const isEmpty = visibleRuns.length === 0;

  const showSkeleton = isInitialLoading;
  const showErrorState = !showSkeleton && isLoadError;
  const showFirstRunState = !showSkeleton && !showErrorState && isEmpty && !hasActiveFilters;
  const showNoMatchState = !showSkeleton && !showErrorState && isEmpty && hasActiveFilters;
  const showList = !showSkeleton && !showErrorState && !isEmpty;

  useEffectSafeStrictMode(() => {
    dispatch(loadRunsForOwnerAction({ ownerKind, ownerId }));
  }, [dispatch, ownerKind, ownerId, slicedFilters]);

  return (
    <PageBlock title={RUN_HISTORY_TITLE}>
      <Box sx={filterBarStyles}>
        <Box sx={filterTopRowStyles}>
          {FILTER_FIELDS.map(field => (
            <FilterField
              dataTestId={filterFieldDataTestIdsByKey[field.key]}
              field={field}
              key={field.key}
              options={filterFieldOptionsByKey[field.key]}
              placeholder={filterFieldPlaceholdersByKey[field.key]}
              value={filterValuesByKey[field.key]}
              onChange={filterChangeHandlersByKey[field.key]}
            />
          ))}
        </Box>

        <Box sx={chipRowStyles}>
          <Box data-testid={AiActivityTestIds.STATUS_FILTER} sx={statusGroupStyles}>
            {STATUS_FILTER_OPTIONS.map(option => (
              <StatusFilterChip
                isActive={option.value === currentStatus}
                key={option.value}
                label={option.label}
                value={option.value}
                onSelect={onStatusChipClick}
              />
            ))}
          </Box>
          <Box sx={savedChipRowStyles}>
            <Typography variant="regular12">{SAVED_CHIP_LABEL_PREFIX}</Typography>
            {SAVED_FILTER_CHIPS.map(chip => (
              <StatusFilterChip
                isActive={savedChips.includes(chip.id)}
                key={chip.id}
                label={chip.label}
                value={chip.id}
                onSelect={onSavedChipToggle(chip.id)}
              />
            ))}
            <StatusFilterChip
              isActive={savedChips.includes(SavedFilterChipId.NEEDS_REVIEW)}
              label={formatNeedsReviewChipLabel(needsReviewCount)}
              value={SavedFilterChipId.NEEDS_REVIEW}
              onSelect={onSavedChipToggle(SavedFilterChipId.NEEDS_REVIEW)}
            />
          </Box>
          <Box data-testid={AiActivityTestIds.GROUP_BY_TOGGLE} sx={groupByToggleRowStyles}>
            <Typography variant="regular12">Group by</Typography>
            <StatusFilterChip
              isActive={groupBy === GroupByMode.DATE}
              label="Date"
              value={GroupByMode.DATE}
              onSelect={onGroupByDate}
            />
            <StatusFilterChip
              isActive={groupBy === GroupByMode.PROCESS}
              label="Process"
              value={GroupByMode.PROCESS}
              onSelect={onGroupByProcess}
            />
          </Box>
        </Box>
      </Box>

      {showSkeleton && (
        <Box data-testid={AiActivityTestIds.RUN_HISTORY_SKELETON}>
          <Skeleton
            height={RUN_SKELETON_ROW_HEIGHT}
            length={RUN_SKELETON_ROW_COUNT}
            type={SKELETON_TYPES.LIST}
          />
        </Box>
      )}

      {showErrorState && (
        <AiActivityEmptyState
          actionLabel={RETRY_LABEL}
          dataTestId={AiActivityTestIds.RUN_HISTORY_ERROR_STATE}
          subtitle={ERROR_STATE_SUBTITLE}
          title={ERROR_STATE_TITLE}
          onAction={onRetryLoad}
        />
      )}

      {showFirstRunState && (
        <AiActivityEmptyState
          actionLabel={isStartAllowed ? START_PROCESS_LABEL : null}
          dataTestId={AiActivityTestIds.FIRST_RUN_STATE}
          subtitle={FIRST_RUN_SUBTITLE}
          title={FIRST_RUN_TITLE}
          onAction={isStartAllowed ? onStartAiProcessClick : null}
        />
      )}

      {showNoMatchState && (
        <AiActivityEmptyState
          actionLabel={CLEAR_FILTERS_LABEL}
          dataTestId={AiActivityTestIds.EMPTY_STATE}
          subtitle={EMPTY_STATE_SUBTITLE}
          title={EMPTY_STATE_TITLE}
          onAction={onClearFilters}
        />
      )}

      {showList && groupBy === GroupByMode.DATE && (
        <Box data-testid={AiActivityTestIds.RUN_HISTORY_LIST}>
          {dateGroups.map(group => (
            <Box key={group.key} sx={groupSectionStyles}>
              <Box sx={groupHeaderStyles}>
                <Typography sx={groupTitleStyles} variant="medium12">
                  {DateGroupLabel[group.key]}
                </Typography>
                <Typography sx={groupMetaStyles} variant="regular12">
                  {formatRunCount(group.runs.length)}
                </Typography>
              </Box>
              <Box sx={rowsListStyles}>{group.runs.map(renderRunRow)}</Box>
            </Box>
          ))}
        </Box>
      )}

      {showList && groupBy === GroupByMode.PROCESS && (
        <Box data-testid={AiActivityTestIds.RUN_HISTORY_LIST}>
          {processGroups.map(group => {
            const stats = computeProcessGroupStats(group.runs);
            const failedCount = stats.statusCounts[AiProcessExecutionStatus.FAILED] ?? 0;
            const totalRuns = group.runs.length;

            return (
              <Box key={group.processId} sx={groupSectionStyles}>
                <Box sx={groupHeaderStyles}>
                  <Typography sx={groupTitleStyles} variant="medium12">
                    {group.name}
                  </Typography>
                  <Typography sx={groupMetaStyles} variant="regular12">
                    {totalRuns} runs · {failedCount} problems
                    {!_.isNil(stats.medianSeconds) &&
                      ` · typically ${formatDurationSeconds(stats.medianSeconds)}`}
                  </Typography>
                </Box>
                <Box sx={rowsListStyles}>{group.runs.map(renderRunRow)}</Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={paginationRowStyles}>
        <Typography variant="regular12">{pagerLabel}</Typography>
        <Box sx={pagerButtonsStyles}>
          <Button
            data-testid={RunHistoryCardTestIds.PREV_PAGE_BUTTON}
            disabled={currentPage === 0}
            variant="outlined"
            onClick={onPrevPage}
          >
            Previous
          </Button>
          <Button
            data-testid={RunHistoryCardTestIds.NEXT_PAGE_BUTTON}
            disabled={
              loadStatus === 'loading' ||
              (!_.isNil(totalCount) && currentSkip + currentTake >= totalCount)
            }
            variant="outlined"
            onClick={onNextPage}
          >
            Next
          </Button>
        </Box>
      </Box>
    </PageBlock>
  );
};

RunHistoryCard.propTypes = {
  filters: PropTypes.shape({
    status: PropTypes.string,
    processId: PropTypes.string,
    startedAfter: PropTypes.string,
    startedBefore: PropTypes.string,
    skip: PropTypes.number,
    take: PropTypes.number,
    search: PropTypes.string,
    groupBy: PropTypes.string,
  }).isRequired,
  ownerKind: PropTypes.string.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  onSelectExecution: PropTypes.func.isRequired,
  ownerId: PropTypes.string,
  isStartAllowed: PropTypes.bool,
  onStartAiProcessClick: PropTypes.func,
};

export default memo(RunHistoryCard);
